import { create } from 'zustand';

/**
 * How abbreviations are shown.
 *
 * - `hints`: as written, marked, and explained on hover or tap. The default, because it teaches the
 *   abbreviation the reader is about to meet again on the НАП portal.
 * - `full`: written out wherever that reads naturally, hints on whatever stays abbreviated.
 * - `plain`: as written and unmarked, for the accountant who finds the underlines patronising.
 */
export type TermsMode = 'hints' | 'full' | 'plain';

export const TERMS_MODES: TermsMode[] = ['hints', 'full', 'plain'];

export const TERMS_STORAGE_KEY = 'danak.terms';

/* Kept in the browser, beside the theme and the language, rather than on the account. Nothing on
   the server reads it, and the visitors it helps most — someone trying the public calculator before
   they have an account — have no account to keep it on. */
function initialMode(): TermsMode {
  try {
    const stored = localStorage.getItem(TERMS_STORAGE_KEY);

    return TERMS_MODES.includes(stored as TermsMode) ? (stored as TermsMode) : 'hints';
  } catch {
    return 'hints';
  }
}

interface TermsState {
  mode: TermsMode;
  setMode: (mode: TermsMode) => void;
}

export const useTermsStore = create<TermsState>((set) => ({
  mode: initialMode(),
  setMode: (mode) => {
    try {
      localStorage.setItem(TERMS_STORAGE_KEY, mode);
    } catch {
      /* private mode: the choice just will not persist */
    }
    set({ mode });
  },
}));
